import { CSSProperties, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import dayjs from 'dayjs';
import { PuffLoader } from 'react-spinners';
import { useApplication } from '../../context/ApplicationContext';
import { Icons } from '../../assets';
import { VisitsLineChart } from './components/visitsPage/components/VisitsLineChart';
import { useSelector } from 'react-redux';
import { authInfoSelect } from '../../entities/auth';
import OriginTable from './components/visitsPage/components/VisitsTable';
import {
    StyledSelect,
    Wrapper,
    Header,
    StyledRangePicker,
    StyledHeadTitle,
    StyledCaledarWrapper,
    ChartsWrapper,
} from './components/visitsPage/styles';
import {
    groupByTemplate,
    fillMissingDates,
    prepareDataWithMissingDates,
} from './components/visitsPage/components/consts';

moment().format();

interface IClientsVisitsData {
    date: string;
    balance: number;
    bonus: number;
    count: number;
    cpColorBg?: string;
    cpColorBorder?: string;
    cpColorText?: string;
    sum: number;
    template: string;
    unique: number;
}

const optionsforSelect = [
    { value: 'unique', label: 'Посещения' },
    { value: 'bonus', label: 'Списанные бонусы' },
    { value: 'balance', label: 'Списанный баланс' },
];

const VisitsPage = () => {
    const { shop } = useSelector(authInfoSelect);
    const shopId = shop
        ? shop.id
        : JSON.parse(localStorage.getItem('shop') || '{}')?.id;
    const [visitsData, setVisitsData] = useState<IClientsVisitsData[]>([]);

    const [selectValue, setSelectValue] = useState<string>('unique');
    const [dataChart, setDataChart] = useState<any | null>(null);
    const [dataTable, setDataTable] = useState<any | null>(null);
    const [visitsTemplates, setVisitsTemplates] = useState<string[]>([]);
    const [formatedDatesArray, setFormatedDatesArray] = useState<string[]>([]);

    useEffect(() => {
        if (
            visitsData.length &&
            formatedDatesArray.length &&
            visitsTemplates.length
        ) {
            // подготовленные данные для графика
            const filledData = prepareDataWithMissingDates(
                visitsData,
                visitsTemplates,
                formatedDatesArray
            );
            const dataFilteredForChart = {
                key: selectValue,
                range: formatedDatesArray,
                values: visitsTemplates.map(template => {
                    return {
                        key: template,
                        range: formatedDatesArray,
                        values: filledData
                            .filter((o: any) => o.template === template)
                            .map(o => o[selectValue]),
                    };
                }),
            };
            setDataChart(dataFilteredForChart);
        }
    }, [visitsData, selectValue, formatedDatesArray, visitsTemplates]);

    const { dateRange, setDateRange } = useApplication();

    const onDateChange = (date: any, dateString: [string, string]) => {
        if (date) {
            setDateRange({
                from: dateString[0],
                to: dateString[1],
            });
        } else {
            // setHistoryDateRange([])
        }
    };

    const from = useMemo(() => {
        return dateRange && dateRange.from
            ? dateRange.from
            : moment().add(-1, 'month').utc().valueOf();
    }, [dateRange]);
    const to = useMemo(
        () =>
            dateRange && dateRange.to ? dateRange.to : moment().utc().valueOf(),
        [dateRange]
    );

    const urlVisitsData = useMemo(() => {
        if (shopId) {
            return `${
                process.env.REACT_APP_API_URL
            }/shops/${shopId}/loyalty/card-templates/count-usage?from=${moment
                .utc(from)
                .add(3, 'hours')
                .format('YYYY-MM-DD')}&to=${moment
                .utc(to)
                .add(3, 'hours')
                .format('YYYY-MM-DD')}`;
        }
    }, [from, to, shopId]);

    useEffect(() => {
        if (shopId && urlVisitsData) {
            fetch(urlVisitsData, {
                // credentials: 'include',
                headers: {
                    accept: 'application/json',
                    Authorization: `${localStorage.getItem('accessToken')}`,
                    'Content-Type': 'application/json',
                },
            })
                .then(res => res.json())
                .then(data => {
                    if (data) setVisitsData(data);
                });
        }
    }, [urlVisitsData, shopId]);

    const loading = false;

    useEffect(() => {
        if (visitsData) {
            const datesArr: string[] = visitsData.map(
                (item: IClientsVisitsData) => item.date
            );
            const uniqueSortedDates: string[] = Array.from(new Set(datesArr));
            // уникальные значения template
            const templatesArr: string[] = visitsData.map(
                (item: IClientsVisitsData) => item.template
            );
            const uniqueTemplates: string[] = Array.from(new Set(templatesArr));
            // подготовленные данные для таблицы
            const dt = Object.values(groupByTemplate(visitsData));

            setDataTable(dt);
            setFormatedDatesArray(uniqueSortedDates);
            setVisitsTemplates(uniqueTemplates);
        }
    }, [visitsData, from, to]);

    const cssOverride: CSSProperties = {
        position: 'absolute',
        top: '50%',
        left: '50%',
    };

    const handleChange = (value: any) => {
        setSelectValue(value);
    };

    return (
        <Wrapper>
            <Header>
                <StyledHeadTitle>Посещения</StyledHeadTitle>
                <StyledCaledarWrapper>
                    <StyledRangePicker
                        suffixIcon={
                            <img
                                src={Icons.IconMainCalendar}
                                alt="icon calendar"
                            />
                        }
                        value={[dayjs(from), dayjs(to)]}
                        format={['YYYY-MM-DD', 'YYYY-MM-DD']}
                        onChange={onDateChange}
                    />
                </StyledCaledarWrapper>
            </Header>
            {loading ? (
                <PuffLoader color="#E65E20" cssOverride={cssOverride} />
            ) : (
                <div>
                    {dataChart && (
                        <ChartsWrapper>
                            <StyledSelect
                                value={selectValue}
                                onChange={handleChange}
                                options={optionsforSelect}
                            />

                            <VisitsLineChart dataSource={dataChart} />
                        </ChartsWrapper>
                    )}
                    {visitsData && dataTable && (
                        <OriginTable dataValues={dataTable} loading={loading} />
                    )}
                </div>
            )}
        </Wrapper>
    );
};

export default VisitsPage;
